import { createContext, useContext, useRef, useState, useEffect, useCallback } from "react";
import { streamUrl } from "@/lib/api";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PlayerContext = createContext(null);
const PREVIEW_LIMIT = 25; // seconds for non-premium

export function PlayerProvider({ children }) {
  const { isPremium } = useAuth();
  const audioRef = useRef(new Audio());
  const [current, setCurrent] = useState(null); // track object
  const [queue, setQueue] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [locked, setLocked] = useState(false); // preview limit hit

  const isPremiumRef = useRef(isPremium);
  useEffect(() => {
    isPremiumRef.current = isPremium;
  }, [isPremium]);

  useEffect(() => {
    const audio = audioRef.current;
    const onTime = () => {
      setProgress(audio.currentTime);
      if (!isPremiumRef.current && audio.currentTime >= PREVIEW_LIMIT) {
        audio.pause();
        setIsPlaying(false);
        setLocked(true);
      }
    };
    const onMeta = () => setDuration(audio.duration || 0);
    const onEnd = () => setIsPlaying(false);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("ended", onEnd);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("ended", onEnd);
    };
  }, []);

  useEffect(() => {
    audioRef.current.volume = volume;
  }, [volume]);

  const playTrack = useCallback((track, list = null) => {
    const audio = audioRef.current;
    setLocked(false);
    if (current && current.id === track.id) {
      if (audio.paused) {
        audio.play();
        setIsPlaying(true);
      } else {
        audio.pause();
        setIsPlaying(false);
      }
      return;
    }
    audio.src = streamUrl(track.id);
    audio.currentTime = 0;
    audio.play().then(() => setIsPlaying(true)).catch(() => {});
    setCurrent(track);
    if (list) setQueue(list);
    api.post(`/tracks/${track.id}/play`).catch(() => {});
  }, [current]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!current) return;
    if (!isPremiumRef.current && audio.currentTime >= PREVIEW_LIMIT) {
      setLocked(true);
      return;
    }
    if (audio.paused) {
      audio.play();
      setIsPlaying(true);
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  }, [current]);

  const seek = useCallback((t) => {
    const audio = audioRef.current;
    if (!isPremiumRef.current && t >= PREVIEW_LIMIT) {
      setLocked(true);
      return;
    }
    audio.currentTime = t;
    setProgress(t);
  }, []);

  const playNext = useCallback(() => {
    if (!queue.length || !current) return;
    const idx = queue.findIndex((t) => t.id === current.id);
    const next = queue[idx + 1];
    if (next) playTrack(next, queue);
  }, [queue, current, playTrack]);

  const playPrev = useCallback(() => {
    if (!queue.length || !current) return;
    const idx = queue.findIndex((t) => t.id === current.id);
    const prev = queue[idx - 1];
    if (prev) playTrack(prev, queue);
  }, [queue, current, playTrack]);

  return (
    <PlayerContext.Provider
      value={{
        current, isPlaying, progress, duration, volume, setVolume, locked, setLocked,
        playTrack, togglePlay, seek, playNext, playPrev, previewLimit: PREVIEW_LIMIT,
      }}
    >
      {children}
    </PlayerContext.Provider>
  );
}

export const usePlayer = () => useContext(PlayerContext);
